import {
    FormModel,
    ValidationSchema,
    Validator,
    validators,
} from 'views/declarations/uk/export/validations/validations';
import { isTemplate } from 'views/declarations/utils/validation-utils';
import { isPostcodeOptional } from './postcodeOptionalCountries';

export const binaryIndicator = (): Validator[] => [
    validators.required(),
    validators.regex(/^[0-1]$/, 'Must be 0 or 1'),
];

export const singleDigit = () => validators.regex(/^[0-9]$/, 'Must be a single digit');

export const twoDigits = () => validators.regex(/^[0-9]{2}$/, 'Must be two digits');

export const dateTime = ({ optional = false }: { optional?: boolean } = {}): ValidationSchema => ({
    childValidators: { dateTime: [validators.required()] },
    ...(optional ? { stopAtNull: true } : {}),
});

export const requiredOn =
    (field: string, message = 'Field is required'): Validator =>
    async (path: string) => ({ [`${path}.${field}`]: message });

/**
 * A place is named either by a UN/LOCODE or by a country and a location. The two are exclusive: once
 * a UN/LOCODE is there the message stops using the country and location altogether (C3003 to C3008),
 * so a place carrying both is reported rather than quietly sent on.
 *
 * Both outcomes are reported on the UN/LOCODE, which is where the rest of the place's errors land.
 */
export const requiredPlace = (): Validator => async (path: string, formModel: FormModel<any>) => {
    const place = formModel.get(path) as
        | { location?: string; unlocode?: string; address?: { country?: string } }
        | undefined;

    if (place?.unlocode) {
        if (!place.location && !place.address?.country) return undefined;

        return requiredOn('unlocode', 'Remove the country and location, or clear the UN/LOCODE')(path, formModel);
    }

    if (place?.location && place?.address?.country) return undefined;

    if (isTemplate(path)) return undefined;

    return requiredOn('unlocode', 'Enter a UN/LOCODE, or a country and location')(path, formModel);
};

const isBlank = (value: unknown) => value == null || String(value).trim() === '';

/**
 * R3002: an address names either a street and a number, or a P.O. box. A P.O. box on its own is
 * enough, and one next to a street and number is not reported - the rule only asks for one of the two.
 * C3034: the postcode is mandatory, except for the countries that only have names of localities.
 */
export const addressRules = (): Validator => async (path: string, formModel: FormModel<any>) => {
    if (isTemplate(path)) return undefined;

    const address = (formModel.get(path) ?? {}) as Record<string, string | null | undefined>;
    const errors: Record<string, string> = {};

    if (isBlank(address.poBox)) {
        if (isBlank(address.street)) errors[`${path}.street`] = 'Enter a street and a number, or a P.O. Box';
        if (isBlank(address.number)) errors[`${path}.number`] = 'Enter a number (N/A if there is none), or a P.O. Box';
    }

    // Without a country there is nothing to decide on - the country is reported as missing itself.
    const country = address.country;
    if (!isBlank(country) && isBlank(address.postCode) && !isPostcodeOptional(country!)) {
        errors[`${path}.postCode`] = 'Postcode is required for this country';
    }

    return Object.keys(errors).length > 0 ? errors : undefined;
};

export const address = ({ required = false }: { required?: boolean } = {}): ValidationSchema => ({
    // Only a required address is held to the rules: `required` is what separates an address the
    // message needs from one that is merely validated for the shape of what was entered.
    ...(required ? { selfValidators: [addressRules()] } : {}),
    childValidators: {
        street: [validators.maxLength(70)],
        streetAdditionalLine: [validators.maxLength(70)],
        number: [validators.maxLength(35)],
        poBox: [validators.maxLength(70)],
        postCode: [validators.maxLength(17)],
        city: [...(required ? [validators.required()] : []), validators.maxLength(35)],
        subDivision: [validators.maxLength(35)],
        country: [...(required ? [validators.required()] : []), validators.exact(2)],
    },
});

/**
 * A list of contact details, or - for the notify party - a single one.
 *
 * The single form deliberately carries no stop condition: where a party names exactly one means of
 * contact the message marks it mandatory (1..1), so an absent one has to be reported. The party
 * itself is what decides whether any of this is looked at, through its own stop condition.
 * `required` therefore has nothing left to say in the single form and is ignored there.
 */
export const communication = ({
    required = false,
    single = false,
}: { required?: boolean; single?: boolean } = {}): ValidationSchema => ({
    childValidators: {
        identifier: [validators.required(), validators.maxLength(512)],
        type: [validators.required(), validators.maxLength(3)],
    },
    ...(single ? {} : { ...(required ? { selfValidators: [validators.minLength(1)] } : {}), stopAtNull: true }),
});

export const party = ({
    required = false,
    eoriRequired = false,
    hasTypeOfPerson = !eoriRequired,
    hasStatus = false,
    communicationRequired = false,
    singleCommunication = false,
}: {
    required?: boolean;
    eoriRequired?: boolean;
    hasTypeOfPerson?: boolean;
    hasStatus?: boolean;
    communicationRequired?: boolean;
    singleCommunication?: boolean;
} = {}): ValidationSchema => ({
    childValidators: {
        name: [validators.required(), validators.maxLength(70)],
        identificationNumber: [...(eoriRequired ? [validators.required()] : []), validators.maxLength(17)],
        ...(hasTypeOfPerson ? { typeOfPerson: [validators.required(), singleDigit()] } : {}),
        ...(hasStatus ? { status: [validators.required(), singleDigit()] } : {}),
        address: address({ required: true }),
        communication: communication({ required: communicationRequired, single: singleCommunication }),
    },
    // An optional party is only looked at once something has been entered into it. `stopAtEmpty`
    // rather than `stopAtNull`: clearing the fields by hand leaves an object of nulls behind, and
    // that is still a party the user has not filled in.
    ...(required ? {} : { stopAtEmpty: true }),
});

export const place = ({ required = false }: { required?: boolean } = {}): ValidationSchema => ({
    childValidators: {
        location: [validators.maxLength(35)],
        unlocode: [validators.maxLength(17)],
        address: { childValidators: { country: [validators.exact(2)] } },
    },
    ...(required ? { selfValidators: [requiredPlace()] } : { stopAtNull: true }),
});

export const transportEquipment: ValidationSchema = {
    childValidators: {
        containerIdentificationNumber: [validators.required(), validators.maxLength(17)],
        containerSizeAndType: [validators.required(), validators.maxLength(10)],
        containerPackedStatus: [validators.required(), validators.maxLength(3)],
        containerSupplierType: [validators.required(), validators.maxLength(3)],
        seal: {
            childValidators: { identifier: [validators.required(), validators.maxLength(20)] },
            stopAtNull: true,
        },
    },
    stopAtNull: true,
};

export const supportingDocuments: ValidationSchema = {
    childValidators: {
        referenceNumber: [validators.required(), validators.maxLength(70)],
        type: [validators.required(), validators.maxLength(4)],
    },
    stopAtNull: true,
};

export const additionalInformation: ValidationSchema = {
    childValidators: {
        code: [validators.maxLength(17)],
        text: [validators.maxLength(512)],
    },
    stopAtNull: true,
};

export const additionalSupplyChainActor: ValidationSchema = {
    childValidators: {
        identificationNumber: [validators.required(), validators.maxLength(17)],
        role: [validators.required(), validators.maxLength(3)],
    },
    stopAtNull: true,
};

export const requiredTransportDocument: ValidationSchema = {
    childValidators: {
        documentNumber: [validators.required(), validators.maxLength(70)],
        type: [validators.required(), validators.maxLength(4)],
    },
};

export const referenceNumberUCR: ValidationSchema = {
    childValidators: { referenceNumberUCR: [validators.maxLength(35)] },
    stopAtNull: true,
};

export const transportCharges = ({ required = false }: { required?: boolean } = {}): ValidationSchema => ({
    childValidators: {
        methodOfPayment: [
            ...(required ? [validators.required()] : []),
            validators.regex(/^[a-zA-Z]$/, 'Must be a single letter'),
        ],
    },
    ...(required ? {} : { stopAtNull: true }),
});

export const passiveBorderTransportMeans = ({
    required = false,
    nationalityRequired = true,
}: { required?: boolean; nationalityRequired?: boolean } = {}): ValidationSchema => ({
    childValidators: {
        identificationNumber: [validators.required(), validators.maxLength(35)],
        typeOfIdentification: [validators.required(), twoDigits()],
        typeOfMeansOfTransport: [validators.required(), validators.maxLength(4)],
        nationality: [...(nationalityRequired ? [validators.required()] : []), validators.exact(2)],
    },
    ...(required ? { selfValidators: [validators.minLength(1)] } : {}),
    stopAtNull: true,
});

export const countriesOfRouting = (): ValidationSchema => ({
    childValidators: {
        sequenceNumber: [validators.number(), validators.maxLength(2)],
        country: [validators.required(), validators.exact(2)],
    },
    selfValidators: [validators.minLength(2)],
    stopAtNull: true,
});

export const representative: ValidationSchema = party({
    eoriRequired: true,
    hasTypeOfPerson: false,
    hasStatus: true,
    communicationRequired: true,
});

export const customsOfficeOfFirstEntry: ValidationSchema = {
    childValidators: { referenceNumber: [validators.required(), validators.exact(8)] },
};

export const activeBorderTransportMeans = (): ValidationSchema => ({
    childValidators: {
        identificationNumber: [validators.required(), validators.maxLength(35)],
        typeOfIdentification: [validators.required(), twoDigits()],
        typeOfMeansOfTransport: [validators.required(), validators.maxLength(4)],
        nationality: [validators.required(), validators.exact(2)],
        modeOfTransport: [validators.required(), singleDigit()],
        estimatedDateAndTimeOfDeparture: dateTime({ optional: true }),
        estimatedDateAndTimeOfArrival: dateTime(),
        actualDateAndTimeOfDeparture: dateTime({ optional: true }),
        countriesOfRoutingOfMeansOfTransport: countriesOfRouting(),
    },
});

export const goodsItemChildValidators: Record<string, Validator[] | ValidationSchema> = {
    goodsItemNumber: [validators.number(), validators.maxLength(5)],
    commodity: {
        childValidators: {
            descriptionOfGoods: [validators.required(), validators.maxLength(512)],
            cusCode: [validators.maxLength(9)],
            commodityCode: {
                childValidators: {
                    harmonizedSystemSubHeadingCode: [validators.required(), validators.exact(6)],
                    combinedNomenclatureCode: [validators.exact(2)],
                },
                stopAtNull: true,
            },
            dangerousGoods: {
                childValidators: { unNumber: [validators.required(), validators.exact(4)] },
                stopAtNull: true,
            },
        },
    },
    weight: {
        childValidators: { grossMass: [validators.required(), validators.float(16, 6)] },
    },
    packaging: {
        childValidators: {
            typeOfPackages: [validators.required(), validators.maxLength(2)],
            numberOfPackages: [validators.number(), validators.maxLength(8)],
            shippingMarks: [validators.maxLength(512)],
        },
        selfValidators: [validators.minLength(1)],
        stopAtNull: true,
    },
    transportEquipment,
    supportingDocuments,
    additionalInformation,
    additionalSupplyChainActor,
};

export const requiredLevel = (schema: ValidationSchema): ValidationSchema => ({
    ...schema,
    selfValidators: [validators.minLength(1)],
    stopAtNull: true,
});
